/**
 * The result card's state: the view model the store already derives
 * (`selectResultView`), whether the card is still on screen, and the things the
 * card can do.
 *
 * "Review" navigates through `rail/navigateRef`, never `useNavigation` — the
 * overlay sits over the Stage and has to stay context-free (UX spec §6a).
 *
 * "Rematch" is `challenge_game_request` with the colours swapped
 * (`actions.rematch`, M2 Task B); its condition is `result.canRematch`, which is
 * PlayChess's own button condition (L5203-5205, plus Deviation 22).
 *
 * `selectResultView` being "the one subscription" (selectors.ts) means one
 * STORE-DRIVEN RE-RENDER, not one `useLiveGame` call: the extra selectors below
 * read action getters off `state.actions`, which is built once and never
 * replaced, so they can never fire a re-render. Only the result view, the watch
 * end, the arena block (and `useV2Ui`'s flags, a different store) can — and the
 * arena block is gated on a result existing at all (E3), so the arena's own
 * status pushes cost a game in progress nothing.
 *
 * M4 adds THREE faces the card did not have:
 *  - `section` — 'swiss' adds the old modal's "standings are updated every 30
 *    seconds" line plus a "Go to tournament" CTA; 'arena' adds the 🔥 streak /
 *    ⚡ berserk chips, the "Next pairing in Ns" countdown, "Go to tournament",
 *    "Stay here" and a Resume/Pause toggle. NOTE this repo's old
 *    `ResultComponent` has NO arena block at all (only `gamePlayMode ==
 *    'tournament'` tweaks) — the arena block is a deliberate improvement taken
 *    from the app lane, recorded in `PARITY.md` M4;
 *  - `spectator` — a WATCHED game has ended. There is no `game.result` for it at
 *    all (the watch slice carries its own `ended{endType,outcome,message}`), so
 *    the card is built from that instead, 1.5 s after the end lands
 *    (`useV2Ui.watchResultReady`, armed by `hooks/useTournamentEffects`). Its
 *    only exit is leaving the watch;
 *  - no play-again row for the tournament FAMILY (old `ResultComponent`
 *    L611-613 on the app side; this repo's modal simply has no rematch inside a
 *    tournament either).
 */
import {useCallback} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {selectResultView, selectWatchEnded} from '@/Store/liveGame/selectors';

import {useGameActions} from '../../rail/actions/useGameActions';
import {takeWatchSourceTab} from '../../rail/watchSourceTab';
import {useV2Ui} from '../../theme/useV2Ui';
import {
  deltaTone,
  outcomeWord,
  resultLineFor,
  scoreFor,
  spectatorMessage,
  spectatorScore,
  spectatorWord,
} from '../format';

/** which extra block the card carries. */
export type ResultSection = 'none' | 'swiss' | 'arena';

/** the play-again row belongs to a CASUAL result; a tournament has sections. */
const CASUAL = (gamePlayMode: string): boolean =>
  gamePlayMode !== 'tournament' && gamePlayMode !== 'arena';

const sectionFor = (gamePlayMode: string): ResultSection => {
  if (gamePlayMode === 'arena') {
    return 'arena';
  }
  return gamePlayMode === 'tournament' ? 'swiss' : 'none';
};

/**
 * The spectator card's outcome, i.e. which word it leads with. The watch-end
 * reducer scores a draw as 'draw', an abort as 'abort' and either side's win by
 * colour — the spectator has no side, so the WINNER is named in the message
 * instead.
 */
const watchOutcome = (
  outcome: 'white' | 'black' | 'draw' | 'abort' | null,
): 'Win' | 'Draw' | 'Abort' => {
  if (outcome === 'draw') {
    return 'Draw';
  }
  return outcome === 'abort' ? 'Abort' : 'Win';
};

/** fix AF: the two seat names of the watched game ('' when unknown) */
const selectWatchNames = (s: LiveGameState) => ({
  white: s.watch.white?.name ?? '',
  black: s.watch.black?.name ?? '',
});

export type ResultView = ReturnType<typeof selectResultView>;

/** the arena block's three inputs */
export type ArenaBlock = {streak: number; berserked: boolean; paused: boolean};

/** the arena chips of a card that is not on screen — one frozen object */
const NO_ARENA: ArenaBlock = {streak: 0, berserked: false, paused: false};

/**
 * E3: the arena block is read ONLY once the game has a result.
 *
 * `arena_status_response` lands every few seconds for every player in the pool,
 * and the pool's size, my points and my pause state all live in it. Subscribing
 * to those while a game is being PLAYED re-rendered this model (and therefore
 * the overlay host) on every push, for a card that cannot be visible yet.
 */
export const selectArenaBlock = (s: LiveGameState): ArenaBlock =>
  s.game.result
    ? {
        streak: Number(s.arena.status?.streak) || 0,
        berserked: s.arena.lastGameBerserk,
        paused:
          !!s.arena.status?.paused || s.arena.status?.myState === 'paused',
      }
    : NO_ARENA;

export type ResultOverlayModel = {
  /** the one `useShallow`'d object of the unit (unit convention §1) */
  result: ResultView;
  visible: boolean;
  /** which token the rating delta takes its colour from */
  delta: 'pos' | 'danger' | 'muted';
  canReview: boolean;
  /** the rating card at all — a spectator has no rating in this game */
  showRating: boolean;
  /** the "New game" button (a casual result only) */
  canPlayAgain: boolean;
  /** its label — "New 3 + 2", the time control that just finished */
  newGameLabel: string;
  canRematch: boolean;
  myColor: 'white' | 'black';
  opponentColor: 'white' | 'black';
  /** the tournament / arena block under the result line (M4) */
  section: ResultSection;
  /** a WATCHED game's end, in spectator wording (M4) */
  spectator: boolean;
  /**
   * Fix AF: the Thunder word, the score and the small result line — by MY
   * outcome for my own game, by the WINNER'S COLOUR for a watched one (a
   * spectator never reads "YOU WON").
   */
  headline: string;
  score: string;
  line: string;
  /** arena only: the chips and the pause state of the "what next" row */
  arenaStreak: number;
  arenaBerserked: boolean;
  arenaPaused: boolean;
  onClose: () => void;
  onNewGame: () => void;
  onReview: () => void;
  onRematch: () => void;
  /** "Go to tournament" — drop the board and land on the right tab */
  onGoToTournament: () => void;
  /** arena only: the Resume / Pause toggle of the result card */
  onArenaPauseToggle: () => void;
  /** the spectator card's only exit */
  onStopWatching: () => void;
};

export function useResultOverlayModel(): ResultOverlayModel {
  const liveResult = useLiveGame(useShallow(selectResultView));
  const gamePlayMode = useLiveGame(s => s.game.gamePlayMode);
  // `game_end_response` keeps the control the finished game was played at
  // (L3038-3039), which is what "play another one of these" needs.
  const tcMs = useLiveGame(s => s.game.tcMs);
  const dismissed = useV2Ui(s => s.resultDismissed);
  const setResultDismissed = useV2Ui(s => s.setResultDismissed);

  // ── M4: the tournament / arena / spectator inputs ──────────────────────
  const {
    streak: arenaStreak,
    berserked: arenaBerserked,
    paused: arenaPaused,
  } = useLiveGame(useShallow(selectArenaBlock));
  const watchEnded = useLiveGame(useShallow(selectWatchEnded));
  // fix AF: the watched seats' names, for "Bala won the match!" — two strings,
  // shallow-compared, written once per watch snapshot
  const watchNames = useLiveGame(useShallow(selectWatchNames));
  const watchReady = useV2Ui(s => s.watchResultReady);
  const setRailTab = useV2Ui(s => s.setRailTab);
  const setViewPly = useV2Ui(s => s.setViewPly);
  /**
   * FIX D / R2: the four post-game callbacks are `rail/actions/useGameActions`
   * now — the rail's icon row needs the same ones, and two copies of "New game
   * re-seeks the control that just finished" is how the card and the lobby got
   * out of step in M1a (P1-3).
   */
  const {newGameLabel, onNewGame, onRematch, onReview, onGoToTournament} =
    useGameActions();
  const stopWatchingGame = useLiveGame(s => s.actions.stopWatchingGame);
  const arenaPause = useLiveGame(s => s.actions.arenaPause);
  const arenaResume = useLiveGame(s => s.actions.arenaResume);

  const spectator = !!watchEnded && watchReady;
  /**
   * The card's view model. A spectator's has no rating, no uuid of mine and no
   * rematch: the shape is the same so the View stays one component.
   */
  const result: ResultView =
    spectator && watchEnded
      ? {
          outcome: watchOutcome(watchEnded.outcome),
          endType: watchEnded.endType,
          message: spectatorMessage(
            watchEnded.outcome,
            watchEnded.message,
            watchNames,
          ),
          myRating: null,
          myRatingDelta: 0,
          opponentRating: null,
          gameUuid: '',
          resultKey: `watch:${watchEnded.endType}`,
          myName: '',
          myColor: null,
          opponentName: '',
          canRematch: false,
        }
      : liveResult;

  const onClose = useCallback(
    () => setResultDismissed(true),
    [setResultDismissed],
  );

  /**
   * The arena card's Resume / Pause. The optimistic `setStatus({paused})` the
   * old screen writes (index.js `handleArenaPauseToggle`) is the REDUCER's job
   * here: the `arena_status_response` the send provokes carries the truth, and
   * the two arena actions are the only writers.
   */
  const onArenaPauseToggle = useCallback(() => {
    if (arenaPaused) {
      arenaResume();
    } else {
      arenaPause();
    }
  }, [arenaPaused, arenaPause, arenaResume]);

  /**
   * Leaving the WATCH: the same single exit path the toolbar's "Stop watching"
   * tile uses — restore the tab the watch was started from, drop the browsed
   * ply, and reset the slice without waiting for the echo.
   */
  const onStopWatching = useCallback(() => {
    setResultDismissed(false);
    setRailTab(takeWatchSourceTab());
    setViewPly(null);
    stopWatchingGame();
  }, [setResultDismissed, setRailTab, setViewPly, stopWatchingGame]);

  const gameUuid = result?.gameUuid;

  const myColor = result?.myColor ?? 'white';

  return {
    result,
    // the spectator card is not dismissible with the ×: its only exit is leaving
    // the watch, or there would be a board on screen with no way back
    visible: spectator ? true : !!result && !dismissed,
    delta: deltaTone(result?.myRatingDelta ?? 0),
    canReview: !spectator && !!gameUuid && result?.outcome !== 'Abort',
    showRating: !spectator,
    // tcMs > 0 as well: a restored game whose control never arrived cannot be
    // re-seeked, and an unlabelled "New " button is worse than none
    canPlayAgain: !spectator && !!result && CASUAL(gamePlayMode) && tcMs > 0,
    newGameLabel,
    canRematch: !spectator && !!result?.canRematch && CASUAL(gamePlayMode),
    myColor,
    opponentColor: myColor === 'white' ? 'black' : 'white',
    section: spectator ? 'none' : sectionFor(gamePlayMode),
    spectator,
    headline:
      spectator && watchEnded
        ? spectatorWord(watchEnded.outcome)
        : outcomeWord(result?.outcome ?? 'Abort'),
    score:
      spectator && watchEnded
        ? spectatorScore(watchEnded.outcome)
        : scoreFor(result?.outcome ?? 'Abort'),
    line: resultLineFor(
      result?.endType ?? '',
      spectator && watchEnded
        ? spectatorScore(watchEnded.outcome)
        : scoreFor(result?.outcome ?? 'Abort'),
    ),
    arenaStreak,
    arenaBerserked,
    arenaPaused,
    onClose,
    onNewGame,
    onReview,
    onRematch,
    onGoToTournament,
    onArenaPauseToggle,
    onStopWatching,
  };
}
